import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Snackbar, Alert } from "@mui/material";
import TopSection from "../../../CommonComponents/TopSection";
import InventoryIcon from "@mui/icons-material/Inventory";
import Toolbar from "../../../CommonComponents/common/Toolbar";
import CollapsibleHeader from "../../../CommonComponents/common/CollapsibleHeader";
import DetailsPageLowerLayer from "../../../Components/DetailsPageLowerLayer/DetailsPageLowerLayer";
import InventoryHeader from "./InventoryHeader";
import OnHandModal from "../../../Components/DetailsPageUpperLayer/OnHandModal";
import TotalStockModal from "../../../Components/DetailsPageUpperLayer/TotalStockModal";
import "./InventoryDetail.css";
import { InventoryGraphView } from "./DetailView/InventoryGraphView";
import {
  fetchInventoryDetailsData,
  fetchInventoryOnHand,
  fetchInventoryTotalStock,
  fetchInventoryAlternateLocation,
  fetchInventoryAlternateItems
} from "../../../services/Inventory/InventoryDetailServices";
import SectionSpinner from "../../../CommonComponents/common/Loader/SectionSpinner";
import { AlternateLocationGraphView } from "./DetailView/AlternateLocationGraphView";
import { WhereUsedGraphView } from "./DetailView/WhereUsedGraphView";
import { exceptionColorMap, getProjectionDotColor } from '../../../utils/colorUtils';
import { formatValue } from "../../../utils/currencyUtils";

const SiteCodeCell = (props) => {
  const { value } = props;
  return (
    <div
      style={{
        borderLeft: `5px solid red`,
        paddingLeft: 8,
        display: "flex",
        alignItems: "center",
      }}
    >
      <span
        style={{
          color: "black",
          textDecoration: "none",
          fontWeight: 500,
          cursor: "default",
        }}
      >
        {value}
      </span>
    </div>
  );
};

export const InventoryDetail = () => {
  const breadcrumbArray = ["Inventory", "Inventory List", "Inventory Detail"];
  const navigate = useNavigate();
  const { itemNo, siteCode } = useParams();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const gridRef = useRef();
  const [openOnHandModal, setOpenOnHandModal] = useState(false);
  const [openTotalStockModal, setOpenTotalStockModal] = useState(false);
  const [headerData, setHeaderData] = useState({});
  const [modalSource, setModalSource] = useState(null); // 'header' or 'alternate'
  const [headerOnHandRows, setHeaderOnHandRows] = useState([]);
  const [altOnHandRows, setAltOnHandRows] = useState([]);
  const [totalStockRows, setTotalStockRows] = useState([]);
  const [alternateLocationRows, setAlternateLocationRows] = useState([]);
  const [alternateItemsRows, setAlternateItemsRows] = useState([]);
  const [batchRows, setBatchRows] = useState([]);
  const [selectedCompareSites, setSelectedCompareSites] = useState([]);
  const [selectedCompareItems, setSelectedCompareItems] = useState([]);
  const [compareModeAlternateLocation, setCompareModeAlternateLocation] = useState(false);
  const [compareModeAlternateItems, setCompareModeAlternateItems] = useState(false);
  // Separate loading states for each section
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingOnHandModal, setLoadingOnHandModal] = useState(false);
  const [loadingTotalStockModal, setLoadingTotalStockModal] = useState(false);
  const [loadingAlternateLocation, setLoadingAlternateLocation] = useState(false);
  const [loadingAlternateItems, setLoadingAlternateItems] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "error"
  });

  useEffect(() => {
    if (!itemNo || !siteCode) return;

    const fetchHeaderData = async () => {
      setLoadingDetails(true);
      try {
        const headerResult = await fetchInventoryDetailsData(itemNo, siteCode);
        if (headerResult?.status === 500) {
          setToast({
            open: true,
            message: headerResult?.message || "Error fetching Inventory Details",
            severity: "error"
          });
          return;
        }
        setHeaderData(headerResult);
      } catch (err) {
        console.error("Details API failed:", err);
      } finally {
        setLoadingDetails(false);
      }
    };

    const fetchOnHandData = async () => {
      setModalSource('header');
      setLoadingBatches(true);
      setLoadingOnHandModal(true);
      try {
        const onHandResult = await fetchInventoryOnHand(itemNo, siteCode);
        setHeaderOnHandRows(onHandResult?.onHandItems || []);
        setBatchRows(onHandResult?.onHandItems || []);
      } catch (err) {
        console.error("OnHand API failed:", err);
      } finally {
        setLoadingBatches(false);
        setLoadingOnHandModal(false);
      }
    };

    const fetchTotalStockData = async () => {
      setLoadingTotalStockModal(true);
      try {
        const totalStockResult = await fetchInventoryTotalStock(itemNo, siteCode);
        setTotalStockRows(totalStockResult?.stockItems || []);
      } catch (err) {
        console.error("TotalStock API failed:", err);
      } finally {
        setLoadingTotalStockModal(false);
      }
    };

    const fetchAlternateLocationData = async () => {
      setLoadingAlternateLocation(true);
      try {
        const alternateLocationResult = await fetchInventoryAlternateLocation(itemNo, siteCode);
        setAlternateLocationRows(alternateLocationResult?.alternateLocations || []);
      } catch (err) {
        console.error("Alternate Location API failed:", err);
      } finally {
        setLoadingAlternateLocation(false);
      }
    };
    const fetchAlternateItemsData = async () => {
      setLoadingAlternateItems(true);
      try {
        const alternateItemsResult = await fetchInventoryAlternateItems(itemNo, siteCode);
        setAlternateItemsRows(alternateItemsResult?.alternateItems || []);
      } catch (err) {
        console.error("Alternate Items API failed:", err);
      } finally {
        setLoadingAlternateItems(false);
      }
    };
    // Run details, on hand, & total stock independently in parallel
    fetchHeaderData();
    fetchOnHandData();
    fetchTotalStockData();
    fetchAlternateLocationData();
    fetchAlternateItemsData();
  }, [itemNo, siteCode]);

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("core/inventory/node/inventory");
    }
  };

  const handleOpenOnHandModal = () => setOpenOnHandModal(true);
  const handleCloseOnHandModal = () => setOpenOnHandModal(false);
  const handleOpenTotalStockModal = () => setOpenTotalStockModal(true);
  const handleCloseTotalStockModal = () => setOpenTotalStockModal(false);

  const handleAlternateTabsOnHandClick = useCallback(async (locationSiteCode) => {
    setOpenOnHandModal(true);
    setLoadingOnHandModal(true);
    try {
      // Fetch on hand data for clicked alternate location site
      const onHandAlternateLocRes = await fetchInventoryOnHand(itemNo, locationSiteCode);
      setAltOnHandRows(onHandAlternateLocRes?.onHandItems || []);
    } catch (err) {
      console.error("Alternate Location OnHand API failed:", err);
    } finally {
      setLoadingOnHandModal(false);
    }
  }, [itemNo]);

  const handleCompareSelectionChange = useCallback((params, mode) => {
    const rows = params.api.getSelectedRows();
    setSelectedCompareSites(rows.map(r => r.siteCode));

    if (mode === "alternateItem")
      setSelectedCompareItems(rows.map(r => r.item));
  }, []);

  const toolbarProps = {
    icon: <InventoryIcon />,
    onBack: handleBack,
    isCollapsed,
    toggleHeader: () => setIsCollapsed((prev) => !prev),
    toolbarMeta: {
      heading: headerData?.itemName,
      metaLine: {
        itemId: itemNo,
        siteCode: siteCode,
        lob: headerData?.lob,
      },
    },
  };

  const identifyColor = useCallback((exception) => exceptionColorMap[exception.toLowerCase()] || "#000000", []);

  const onHandAndTotalStockColumns = [
    {
      field: "time",
      headerName: "Time",
      flex: 1,
      cellRenderer: (params) => {
        const { value, data } = params;
        const color = identifyColor(data?.exception);
        return (
          <div
            style={{
              borderLeft: `5px solid ${color}`,
              paddingLeft: 8,
              display: "flex",
              alignItems: "center",
            }}
          >
            <span
              style={{
                color: "black",
                textDecoration: "none",
                fontWeight: 500,
                cursor: "default",
              }}
            >
              {value}
            </span>
          </div>
        );
      },
    },
    {
      field: "quantity", headerName: "Quantity", flex: 1,
      cellRenderer: (params) => formatValue(params.value)
    },
    { field: "stockCategory", headerName: "Batch Status", flex: 1 },
    { field: "batch", headerName: "Batch", flex: 1 },
    {
      field: "exception",
      headerName: "Exception",
      flex: 1,
      cellRenderer: (params) => {
        const { value, data } = params;
        const color = identifyColor(data?.exception);
        return (
          <span
            style={{
              color,
              textDecoration: "none",
              fontWeight: 500,
              cursor: "default",
            }}
          >
            {value}
          </span>
        );
      },
    },
    { field: "expiry", headerName: "Best Before Date", flex: 1 },
    { field: "storageLocation", headerName: "Storage Location", flex: 1 },
  ];

  const parentRef = useRef(null);

  const graphTab = {
    label: "Inventory View",
    component: (
      <InventoryGraphView
        inventoryId={itemNo}
        urestrictedStock={headerData?.unrestricted}
        siteCode={siteCode}
      />
    ),
  };

  const alternateLocationTab = useMemo(
    () => ({
      label: "Alternate Location",
      loading: loadingAlternateLocation,
      toolbar: {
        buttons: !compareModeAlternateLocation ? [
          {
            label: "Compare",
            onClick: () => setCompareModeAlternateLocation(true),
            disabled: selectedCompareSites.length < 1
          }
        ] : [],
      },
      onSelectionChanged: (params) => handleCompareSelectionChange(params, "alternateLocation"),
      columns: compareModeAlternateLocation ? [] : [
        {
          field: "siteCode",
          headerName: "Plant",
          checkboxSelection: true,                 // row checkboxes
          headerCheckboxSelection: true,            // "select all" checkbox in the header
          flex: 1,
          cellRendererFramework: SiteCodeCell
        },
        { field: "siteName", headerName: "Plant Name", flex: 1, },
        {
          field: "onHand",
          headerName: "On Hand",
          flex: 1,
          cellRenderer: (params) => (
            <span
              style={{ color: "#1976d2", cursor: "pointer" }}
              onClick={() => handleAlternateTabsOnHandClick(params.data?.siteCode)}
            >
              {formatValue(params.value)}
            </span>
          ),
        },
        {
          field: "projection6W",
          headerName: "Projection",
          flex: 1,
          cellRenderer: (params) => {
            const { value, data } = params;
            return (
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  justifyContent: "left",
                  marginTop: "12px",
                }}
              >
                {Object.entries(value).map(([key, projectedVal], idx) => {
                  const minStock = Number(data?.minStock6W?.[key]) || 0;
                  const maxStock = Number(data?.maxStock6W?.[key]) || 0;
                  const color = getProjectionDotColor(Number(projectedVal), minStock, maxStock);
                  return (
                    <div
                      key={idx}
                      title={`Week ${idx + 1}: ${formatValue(projectedVal)}`}
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: color,
                        border: "1px solid #ccc",
                        display: "inline-block",
                      }}
                    />
                  );
                })}
              </div>
            );
          },
        }
      ],
      rows: compareModeAlternateLocation ? [] : alternateLocationRows,
      renderCustomContent: compareModeAlternateLocation ? <AlternateLocationGraphView
        inventoryId={itemNo}
        siteCode={siteCode}
        siteCodes={selectedCompareSites}
        onExitCompare={() => {
          setCompareModeAlternateLocation(false);
          setSelectedCompareSites([]);
        }}
        label="alternateLocation"
      /> : null
    }),
    [loadingAlternateLocation, alternateLocationRows, handleAlternateTabsOnHandClick, selectedCompareSites,
      compareModeAlternateLocation, itemNo, siteCode, handleCompareSelectionChange]
  );

  const alternateItemsTab = useMemo(
    () => ({
      label: "Alternate Items",
      loading: loadingAlternateItems,
      toolbar: {
        buttons: !compareModeAlternateItems ? [
          {
            label: "Compare",
            onClick: () => setCompareModeAlternateItems(true),
            disabled: selectedCompareItems.length < 1
          }
        ] : [],
      },
      onSelectionChanged: (params) => handleCompareSelectionChange(params, "alternateItem"),
      columns: compareModeAlternateItems ? [] : [
        {
          field: "siteCode",
          headerName: "Plant",
          checkboxSelection: true,                  // row checkboxes
          headerCheckboxSelection: true,            // "select all" checkbox in the header
          flex: 1,
          cellRendererFramework: SiteCodeCell
        },
        { field: "siteName", headerName: "Plant Name", flex: 1, },
        { field: "item", headerName: "Item", flex: 1, },
        {
          field: "onHand",
          headerName: "On Hand",
          flex: 1,
          cellRenderer: (params) => (
            <span
              style={{ color: "#1976d2", cursor: "pointer" }}
              onClick={() => handleAlternateTabsOnHandClick(params.data?.siteCode)}
            >
              {formatValue(params.value)}
            </span>
          ),
        },
        {
          field: "projection6W",
          headerName: "Projection",
          flex: 1,
          cellRenderer: (params) => {
            const { value, data } = params;
            return (
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  justifyContent: "left",
                  marginTop: "12px",
                }}
              >
                {Object.entries(value).map(([key, projectedVal], idx) => {
                  const minStock = Number(data?.minStock6W?.[key]) || 0;
                  const maxStock = Number(data?.maxStock6W?.[key]) || 0;
                  const color = getProjectionDotColor(Number(projectedVal), minStock, maxStock);
                  return (
                    <div
                      key={idx}
                      title={`Week ${idx + 1}: ${formatValue(projectedVal)}`}
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: color,
                        border: "1px solid #ccc",
                        display: "inline-block",
                      }}
                    />
                  );
                })}
              </div>
            );
          },
        },
        { field: "externalMaterialGroup", headerName: "External Material Group", flex: 1, },
      ],
      rows: compareModeAlternateItems ? [] : alternateItemsRows,
      renderCustomContent: compareModeAlternateItems ? <AlternateLocationGraphView
        inventoryId={itemNo}
        siteCode={siteCode}
        siteCodes={selectedCompareSites}
        inventoryIds={selectedCompareItems}
        onExitCompare={() => {
          setCompareModeAlternateItems(false);
          setSelectedCompareSites([]);
        }}
        label="alternateItem"
      /> : null
    }),
    [loadingAlternateItems, alternateItemsRows, handleAlternateTabsOnHandClick, selectedCompareSites,
      selectedCompareItems, compareModeAlternateItems, itemNo, siteCode, handleCompareSelectionChange]
  );

  const batchTab = useMemo(
    () => ({
      label: "Batches",
      loading: loadingBatches,
      columns: [
        {
          field: "batch",
          headerName: "Batch",
          cellRenderer: (params) => {
            const { value, data } = params;
            const color = identifyColor(data?.exception);
            return (
              <div
                style={{
                  borderLeft: `5px solid ${color}`,
                  paddingLeft: 8,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    color: "black",
                    textDecoration: "none",
                    fontWeight: 500,
                    cursor: "default",
                  }}
                >
                  {value}
                </span>
              </div>
            );
          },
        },
        {
          field: "exception",
          headerName: "Exception",
          cellRenderer: (params) => {
            const { value, data } = params;
            const color = identifyColor(data?.exception);
            return (
              <span
                style={{
                  color,
                  textDecoration: "none",
                  fontWeight: 500,
                  cursor: "default",
                }}
              >
                {value}
              </span>
            );
          },
        },
        { field: "stockCategory", headerName: "Batch Status" },
        { field: "expiry", headerName: "Best Before Date" },
        { field: "storageLocation", headerName: "Storage Location" },
        {
          field: "quantity", headerName: "Quantity",
          cellRenderer: (params) => formatValue(params.value)
        },
      ],
      rows: batchRows,
    }),
    [batchRows, identifyColor, loadingBatches]
  );

  const whereUsedTab = {
    label: "Where Used",
    component: (
      <WhereUsedGraphView
        inventoryId={itemNo}
        siteCode={siteCode}
      />
    ),
  };

  const tabs = [graphTab, alternateLocationTab, alternateItemsTab, batchTab, whereUsedTab];

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshServerSide({ purge: true });
    }
  }, []);

  return (
    <div ref={parentRef}>
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast({ ...toast, open: false })}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
      <TopSection
        breadcrumbArray={breadcrumbArray}
        hideHeader={true}
        headerName={""}
        hideTitle={true}
      />
      <Toolbar {...toolbarProps} />
      <CollapsibleHeader
        upperLayer={
          <Box sx={{ position: "relative" }}>
            {loadingDetails && <SectionSpinner />}
            <InventoryHeader
              hProps={headerData}
              onOnHandClick={handleOpenOnHandModal}
              onTotalStockClick={handleOpenTotalStockModal}
            />
          </Box>
        }
        lowerLayer={<DetailsPageLowerLayer
          tabs={tabs}
          onTabChange={(index) => {
            // Whenever ANY tab changes, reset compare modes in both tabs
            setCompareModeAlternateLocation(false);
            setSelectedCompareSites([]);
            setCompareModeAlternateItems(false);
            setSelectedCompareSites([]);
          }}
        />}
        isCollapsed={isCollapsed}
      />
      <OnHandModal
        open={openOnHandModal}
        handleClose={handleCloseOnHandModal}
        rows={modalSource === 'header' ? headerOnHandRows : altOnHandRows}
        columns={onHandAndTotalStockColumns}
        loading={loadingOnHandModal}
      />
      <TotalStockModal
        open={openTotalStockModal}
        handleClose={handleCloseTotalStockModal}
        rows={totalStockRows}
        columns={onHandAndTotalStockColumns}
        loading={loadingTotalStockModal}
      />
    </div>
  );
};